import { formatFormTime } from "../feedback/format";
import { windowState, type WindowState } from "../feedback/logic";
import type { FeedbackForm } from "../feedback/schemas";

/**
 * A feedback form an announcement links to, judged at the moment it would go
 * out.
 *
 * The backend applies one rule to Send now and to a scheduled send: the form
 * has to accept answers at the send time, which means open and inside its
 * window (form_unsendable_reason in
 * functions/python/app/application/service/event_announcement.py). A form is
 * never opened for the organiser, so these sentences say what they have to do.
 */

export type FormAtSend = WindowState | "missing";

/**
 * How the form stands at `at`. With no time yet (Send later before a valid
 * time is typed) only the open/closed switch can be judged.
 */
export function formAtSend(
  form: FeedbackForm | undefined,
  at: Date | null,
): FormAtSend {
  if (!form) return "missing";
  if (at === null) return form.status === "open" ? "accepting" : "closed";
  return windowState(form, at);
}

export function findForm(
  forms: FeedbackForm[] | null,
  formId: string | undefined,
): FeedbackForm | undefined {
  if (!forms || !formId) return undefined;
  return forms.find((form) => form.id === formId);
}

/** What the picker says under the chosen form, or null when all is well. */
export function pickerWarning(
  form: FeedbackForm | undefined,
  mode: "now" | "later",
  at: Date | null,
  timeZone: string | undefined,
): string | null {
  const state = formAtSend(form, at);
  if (state === "accepting")
    return mode === "later" && closesJustAfter(form, at)
      ? CLOSES_JUST_AFTER
      : null;
  if (state === "missing" || !form)
    return "This feedback form no longer exists. Choose another one, or no form.";
  if (mode === "now") {
    if (state === "closed")
      return "This form is closed, so the announcement would not be sent. Open the form in the Feedback tab first.";
    if (state === "not-yet-open" && form.opensAt)
      return `This form opens ${formatFormTime(form.opensAt, timeZone)}. Until then the announcement would not be sent.`;
    return form.closesAt
      ? `This form closed ${formatFormTime(form.closesAt, timeZone)}, so the announcement would not be sent.`
      : "This form is not taking answers, so the announcement would not be sent.";
  }
  if (state === "closed")
    return "This form is closed. Open it before the send time, or the announcement is not sent. Forms are never opened automatically.";
  if (state === "not-yet-open" && form.opensAt)
    return `This form opens ${formatFormTime(form.opensAt, timeZone)}, after the send time, so the announcement would not be sent.`;
  return form.closesAt
    ? `This form closes ${formatFormTime(form.closesAt, timeZone)}, before the send time, so the announcement would not be sent.`
    : "This form is not taking answers at the send time, so the announcement would not be sent.";
}

/**
 * The scheduler ticks once a minute and a claim can run a little after the
 * send time; the backend judges the form at the claim. A form that closes
 * within this margin after the send time may already be closed by then.
 */
export const CLOSE_MARGIN_MS = 5 * 60_000;

export const CLOSES_JUST_AFTER =
  "The form closes just after the send time, so the announcement may not go out.";

/** Whether the form closes within CLOSE_MARGIN_MS after `at`. */
export function closesJustAfter(
  form: FeedbackForm | undefined,
  at: Date | null,
): boolean {
  if (!form?.closesAt || at === null) return false;
  const gap = form.closesAt.toMillis() - at.getTime();
  return gap > 0 && gap <= CLOSE_MARGIN_MS;
}

/**
 * The warning on a booked announcement whose form would stop it going out, or
 * may stop it because it closes within CLOSE_MARGIN_MS after the send time,
 * the same case the picker warned about when it was booked. Shown until the
 * form accepts answers with room to spare, so it clears by itself once the
 * organiser opens the form or moves its close.
 */
export function scheduledFormWarning(
  form: FeedbackForm | undefined,
  sendAt: Date | null,
): string | null {
  const state = formAtSend(form, sendAt);
  if (state === "accepting")
    return closesJustAfter(form, sendAt) ? CLOSES_JUST_AFTER : null;
  if (state === "missing")
    return "The linked feedback form was deleted. This announcement will not be sent unless you edit it.";
  if (state === "closed")
    return "The linked feedback form is closed. Open it before the send time, or this announcement will not be sent. Forms are never opened automatically.";
  if (state === "not-yet-open")
    return "The linked feedback form opens after the send time, so this announcement would not be sent.";
  return "The linked feedback form closes before the send time, so this announcement would not be sent.";
}

/** "Feedback form: Day one", or what is left of a deleted one. */
export function formLabel(
  forms: FeedbackForm[] | null,
  formId: string,
): string {
  const form = findForm(forms, formId);
  if (form) return form.title;
  return forms === null ? "Loading…" : "Deleted form";
}
